import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";

const JOIN_KEYWORD_QUERY_KEY = ["sms-join-keyword"];

/** The welcome-keyword row whose response_body is the welcome text. */
export interface JoinKeyword {
  id: string;
  store_id: string;
  keyword: string;
  response_body: string;
}

/**
 * Reads the store's welcome keyword — the JOIN row of sms_keywords, whose
 * response_body is the welcome text a customer gets when they reply Y to an
 * invite or text JOIN cold. RLS (sms_apply_store_rls, write-enabled) scopes it
 * to the caller's store, so the authenticated client both reads and edits it.
 * Not in the generated Database types, so it goes through an untyped client.
 */
export const useJoinKeyword = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<JoinKeyword | null>({
    queryKey: JOIN_KEYWORD_QUERY_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb
        .from("sms_keywords")
        .select("id, store_id, keyword, response_body")
        .eq("keyword", "JOIN")
        .maybeSingle();
      if (error) throw error;
      return (data as JoinKeyword | null) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/** Updates the welcome keyword's response_body and invalidates the query. */
export const useUpdateJoinKeyword = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, response_body }: { id: string; response_body: string }) => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { error } = await sb.from("sms_keywords").update({ response_body }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOIN_KEYWORD_QUERY_KEY });
    },
  });
};
